import { useMemo } from 'react';
import styled from 'styled-components';
import { Row, Col, Tag } from 'antd';

import { IFlat } from '../../common/model/flat.model';
import useI18n from '../../common/hooks/useI18n';
import { formatCurrency } from '../../common/helpers';

type Props = {
  flat: IFlat;
  className?: string;
};

const StyledRow = styled(Row)`
  justify-content: flex-end;

  @media ${(props) => props.theme.breakpoints.smd} {
    justify-content: center;
  }
`;

const ImageContainer = styled.div`
  height: 64px;
  @media ${(props) => props.theme.breakpoints.smd} {
    height: 40px;
  }
`;

const ImageFlexContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const Image = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const StyledTagContainer = styled.div`
  display: flex;
`;

const StyledTag = styled(Tag)`
  padding: 22px;
  height: 64px;
  vertical-align: middle;
  display: table-cell;
  font-size: 16px;
  line-height: 16px;

  @media ${(props) => props.theme.breakpoints.smd} {
    padding: 10px;
    height: 40px;
    width: 100%;
  }
`;

const Info = styled.span`
  font-weight: 600;
  font-size: 16px;
  color: black;
`;

const Summary = ({ flat, className }: Props): JSX.Element => {
  const i18n = useI18n();

  const hasRooms = useMemo(() => flat.rooms > 0, [flat.rooms]);
  const hasBathrooms = useMemo(() => flat.bathrooms > 0, [flat.bathrooms]);

  return (
    <Row justify="center" align="top" gutter={[0, 24]} className={className}>
      <Col xs={24} sm={24} md={4} xl={3} style={{ textAlign: 'center' }}>
        <StyledTagContainer>
          <StyledTag color="green">
            <Info>{`${i18n.t('flat.price')}: ${formatCurrency(
              flat.price,
              i18n.activeLocale
            )}`}</Info>
          </StyledTag>
        </StyledTagContainer>
      </Col>

      <Col xs={0} sm={0} md={8} xl={12} />

      <Col xs={24} sm={24} md={12} xl={9}>
        <StyledRow>
          <Col span={8} style={{ textAlign: 'center' }}>
            <ImageContainer>
              <ImageFlexContainer>
                <Image
                  src="/images/sqr_meters.svg"
                  as="img"
                  alt={i18n.t('flat.sqrMeters')}
                  loading="lazy"
                />
              </ImageFlexContainer>
            </ImageContainer>
            <div style={{ marginTop: '1rem' }}>
              <Info>
                {flat.sqrMeters} m
                <sup style={{ verticalAlign: 'top', fontSize: '0.6em' }}>2</sup>
              </Info>
            </div>
          </Col>
          {hasRooms && (
            <Col span={8} style={{ textAlign: 'center' }}>
              <>
                <ImageContainer>
                  <ImageFlexContainer>
                    <Image
                      as="img"
                      src="/images/rooms.svg"
                      alt={i18n.t('flat.rooms')}
                    />
                  </ImageFlexContainer>
                </ImageContainer>
                <div style={{ marginTop: '1rem' }}>
                  <Info>{`${flat.rooms} ${i18n
                    .t('flat.roomsShort')
                    .toLowerCase()}`}</Info>
                </div>
              </>
            </Col>
          )}
          {hasBathrooms && (
            <Col span={8} style={{ textAlign: 'center' }}>
              <ImageContainer>
                <ImageFlexContainer>
                  <Image
                    as="img"
                    src="/images/bathrooms.svg"
                    alt={i18n.t('flat.bathrooms')}
                  />
                </ImageFlexContainer>
              </ImageContainer>
              <div style={{ marginTop: '1rem' }}>
                <Info>{`${flat.bathrooms} ${i18n
                  .t('flat.bathrooms')
                  .toLowerCase()}`}</Info>
              </div>
            </Col>
          )}
        </StyledRow>
      </Col>
    </Row>
  );
};

export default Summary;
